import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import _ from 'lodash';
import type { DashboardBackgroundStyle, DashboardContent, DashboardTile } from './dashboard.types';
import { HEADER_HEIGHT } from './dashboard.types';

/**
 * This store manages the data for dashboards. The 'content' in this store is an Array of DashboardTile objects that
 * combine the data necessary to create the react-grid-layout as well as some information that defines the content
 * of the displayed tiles. The actual tile content pieces (the images and interactive content pieces) are managed by the
 * sqReportStore to ensure that DateRanges and AssetSelection continue to work as expected.
 */
export class DashboardStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqDashboardStore';

  initialize() {
    this.state = this.immutable({
      columnCount: 12,
      content: [],
      pageBackgroundStyle: { mode: 'none' } as DashboardBackgroundStyle,
      defaultTileBackgroundStyle: null as DashboardBackgroundStyle | null,
      pendingInsertPosition: null,
    });
  }

  get content(): DashboardTile[] {
    return this.state.get('content');
  }

  get columnCount(): number {
    return this.state.get('columnCount');
  }

  get pageBackgroundStyle(): DashboardBackgroundStyle | null {
    return this.state.get('pageBackgroundStyle');
  }

  get defaultTileBackgroundStyle(): DashboardBackgroundStyle | null {
    return this.state.get('defaultTileBackgroundStyle');
  }

  get pendingInsertPosition() {
    return this.state.get('pendingInsertPosition');
  }

  protected readonly handlers = {
    // This handler is triggered by a react-grid-layout callback. react-grid-layout strips out custom properties (like
    // the title, id and the type); we need to make sure to only merge the grid changes onto the existing content
    // entries, or we will lose important information).
    DASHBOARD_MERGE_LAYOUT_CHANGES: ({ layout }: { layout: ReactGridLayout.Layout[] }) => {
      const content = this.state.get('content');
      // need to merge onto content:
      this.state.set(
        'content',
        layout.map((gridItem) => {
          const existingItem = _.find(content, { id: gridItem.i });
          return { ...existingItem, ...gridItem };
        }),
      );
    },

    DASHBOARD_ADD_TO_DASHBOARD: ({ tile, heading }: { tile: DashboardTile; heading: boolean }) => {
      let content = this.state.get('content');
      if (heading && tile.y === 0) {
        // Only shift content down if header is being placed at the top (y=0):
        content = content.map((item: DashboardTile) => {
          return { ...item, y: item.y + HEADER_HEIGHT };
        });
      }

      const defaultBackground = this.state.get('defaultTileBackgroundStyle') as DashboardBackgroundStyle | null;
      const tileWithBackground = {
        ...tile,
        backgroundStyle: tile.backgroundStyle ? _.cloneDeep(tile.backgroundStyle) : _.cloneDeep(defaultBackground),
      };

      this.state.set('content', [...content, tileWithBackground]);
    },

    DASHBOARD_UPDATE_TILE: ({ id, property, value }: { id: string; value: unknown; property: string }) => {
      const content = this.state.get('content').find((item: DashboardContent) => item.id === id);
      const updatedContent = [...this.state.get('content')].filter((item: DashboardContent) => item.id !== id);
      this.state.set('content', [...updatedContent, { ...content, [property]: value }]);
    },

    DASHBOARD_UPDATE_TILE_BACKGROUND: ({
      id,
      background,
    }: {
      id: string;
      background: DashboardBackgroundStyle | null;
    }) => {
      const updatedContent = this.state.get('content').map((item: DashboardTile) => {
        if (item.id !== id) {
          return item;
        }
        return { ...item, backgroundStyle: _.cloneDeep(background) };
      });
      this.state.set('content', updatedContent);
    },

    DASHBOARD_APPLY_BACKGROUND_TO_ALL_TILES: ({ background }: { background: DashboardBackgroundStyle | null }) => {
      this.state.set(
        'content',
        this.state.get('content').map((item: DashboardTile) => ({
          ...item,
          backgroundStyle: _.cloneDeep(background),
        })),
      );
      this.state.set('defaultTileBackgroundStyle', _.cloneDeep(background));
    },

    DASHBOARD_SET_DEFAULT_TILE_BACKGROUND: ({ background }: { background: DashboardBackgroundStyle | null }) => {
      this.state.set('defaultTileBackgroundStyle', _.cloneDeep(background));
    },

    DASHBOARD_UPDATE_PAGE_BACKGROUND: ({ background }: { background: DashboardBackgroundStyle | null }) => {
      this.state.set('pageBackgroundStyle', _.cloneDeep(background));
    },

    DASHBOARD_SET_PENDING_INSERT_POSITION: ({ x, y }: { x: number; y: number }) => {
      this.state.set('pendingInsertPosition', { x, y });
    },

    DASHBOARD_CLEAR_PENDING_INSERT_POSITION: () => {
      this.state.set('pendingInsertPosition', null);
    },

    DASHBOARD_REMOVE_CONTENT: ({ id }: { id: string }): void => {
      this.state.set('content', _.reject([...this.state.get('content')], { id }));
    },
  };

  dehydrate() {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: any) {
    this.state.merge(_.omit(dehydratedState, ['pendingInsertPosition']));

    if (_.isUndefined(this.state.get('pageBackgroundStyle'))) {
      this.state.set('pageBackgroundStyle', { mode: 'none' } as DashboardBackgroundStyle);
    }

    if (_.isUndefined(this.state.get('defaultTileBackgroundStyle'))) {
      this.state.set('defaultTileBackgroundStyle', null);
    }
  }
}
